// Direct-Booking Widget — a pill in the site header showing the guest's
// currently-issued coupon code. Persisted in localStorage after they visit
// /redeem/{code}; re-validated against the backend on mount so expired /
// redeemed codes disappear automatically.
import React, { useEffect, useState } from 'react';
import { Tag, Copy, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const LS_KEY = 'rn_active_coupon';

const readStored = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const clearStored = () => {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
};

const DirectBookingWidget = () => {
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (!stored?.code) return;
    // Validate against the backend so expired / redeemed codes vanish.
    (async () => {
      try {
        const r = await fetch(`${API}/api/coupons/${encodeURIComponent(stored.code)}`);
        if (!r.ok) { clearStored(); return; }
        const j = await r.json();
        if (j.status !== 'active') {
          clearStored();
          return;
        }
        setCoupon(j);
      } catch { /* offline — keep localStorage */ }
    })();
  }, []);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  if (!coupon || dismissed) return null;

  return (
    <div
      className="hidden md:flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full pl-3 pr-1 py-1 shadow-lg animate-in fade-in"
      data-testid="direct-booking-widget"
    >
      <Tag className="w-4 h-4 flex-shrink-0" />
      <div className="text-xs leading-tight">
        <p className="font-bold">Save {coupon.discount_pct}% direct</p>
        <p className="opacity-90 font-mono text-[10px]">{coupon.code}</p>
      </div>
      <button
        onClick={onCopy}
        className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
        title="Copy code"
        data-testid="widget-copy"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={() => { clearStored(); setDismissed(true); }}
        className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center"
        title="Dismiss"
        data-testid="widget-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default DirectBookingWidget;
